import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { setTimeZone } from '@ruby-cms/shared';
import { api, lastRefreshCode, onSessionLost, refreshSession, setAccessToken, setClientZone } from '../api/client';

export type Me = {
  id: string;
  username: string;
  displayName: string;
  role: string;
  roleLabel: string;
  permissions: string[];
  // the CMS time zone (setting display.timeZone): every time on every page is shown in it
  timeZone?: string;
};

// the zone is set before the pages render, so no page shows a time in another one
async function loadMe(): Promise<Me> {
  const m = await api.get<Me>('/api/admin/auth/me');
  if (m.timeZone) {
    try { setTimeZone(m.timeZone); setClientZone(m.timeZone); } catch { /* keep the default zone */ }
  }
  return m;
}

type Ctx = {
  me: Me | null;
  ready: boolean;
  // why the login page is shown, when the session was ended by a newer login of the same account elsewhere
  notice: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  can: (perm: string) => boolean;
};

const AuthCtx = createContext<Ctx | null>(null);

// one live session per staff account (admin-1-phien): the API refuses an older session with this code
const REPLACED_NOTICE = 'Tài khoản đã đăng nhập ở nơi khác';
const noticeFor = (code: string) => (code === 'SESSION_REPLACED' ? REPLACED_NOTICE : null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [ready, setReady] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      if (await refreshSession()) {
        try { setMe(await loadMe()); } catch { setMe(null); }
      } else {
        setNotice(noticeFor(lastRefreshCode()));
      }
      setReady(true);
    })();
    return onSessionLost((code) => { setMe(null); setNotice(noticeFor(code)); });
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const r = await api.post<{ accessToken: string; admin: Me }>('/api/admin/auth/login', { username, password });
    setAccessToken(r.accessToken);
    setMe(await loadMe());
    setNotice(null);
  }, []);

  const logout = useCallback(async () => {
    try { await api.post('/api/admin/auth/logout'); } catch { /* session already gone */ }
    setAccessToken(null);
    setMe(null);
    setNotice(null);
  }, []);

  const can = useCallback((p: string) => !!me?.permissions.includes(p), [me]);
  const value = useMemo(() => ({ me, ready, notice, login, logout, can }), [me, ready, notice, login, logout, can]);
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const c = useContext(AuthCtx);
  if (!c) throw new Error('AuthProvider missing');
  return c;
}
